// The prose codec: one screenplay-shaped text format that `read` emits and
// `write` accepts back. One format for the agent to learn, and the round-trip
// is the contract (see `sc selfcheck`).
import { assetName, stateName } from "./model.js";
import { step } from "./trace.js";
const NON_ACTOR = {
    system: "旁白", broadcast: "广播", offscreen: "画外", group: "群声",
};
const THOUGHT = "心声";
function speakerLabel(doc, sp) {
    if (sp.kind)
        return NON_ACTOR[sp.kind] ?? sp.kind;
    const name = assetName(doc, sp.id);
    // Always show a speaker state that exists. Hiding it when the scene "pins it
    // down" saved a few characters and silently dropped 1001 of them on the first
    // whole-script rewrite: a field the view omits is a field write must guess.
    return sp.state ? `${name}·${stateName(doc, sp.id, sp.state)}` : name;
}
export function formatAction(doc, a, gutter, opts = {}) {
    const pad = (opts.plain ? "" : a.anchor).padEnd(gutter);
    const ts = !opts.plain && a.ts ? `${a.ts} ` : "";
    const body = (() => {
        if (a.type === "transition")
            return `→ ${a.content}`;
        if (a.type === "dialogue" || a.type === "inner_thought") {
            const who = a.speaker ? speakerLabel(doc, a.speaker) : "?";
            const notes = [a.type === "inner_thought" ? THOUGHT : "", a.paren ?? ""].filter(Boolean);
            const label = a.speaker?.kind && a.speaker.label ? `${who}·${a.speaker.label}` : who;
            return `${label}${notes.length ? `(${notes.join("/")})` : ""}：${a.content}`;
        }
        return a.content;
    })();
    return `${pad}${pad ? "  " : ""}${ts}${body}`.replace(/\n/g, "\\n");
}
function refText(doc, r) {
    const name = assetName(doc, r.id);
    // The bare name means exactly the lone `default` state. Anything else lists
    // every bound state by name, `default` included — dropping it here would make
    // the header lossy the moment an episode is written back.
    if (r.states.length === 1 && r.states[0] === "default")
        return name;
    return `${name}(${r.states.map((x) => stateName(doc, r.id, x)).join("+")})`;
}
export function formatSceneHead(doc, s) {
    const env = [s.env?.space, s.env?.time].filter(Boolean).join("/").toUpperCase();
    const where = s.location ? refText(doc, s.location) : "—";
    const head = `## ${s.ep.no}-${s.no}  ${s.anchor === `${s.ep.no}-${s.no}` ? s.id : s.anchor}`
        + `  ${env || "—"}  ${where}  ${s.actions.length} actions`;
    const cast = s.actors.map((r) => refText(doc, r)).join(", ");
    const props = s.props.map((r) => refText(doc, r)).join(", ");
    const meta = [cast && `cast: ${cast}`, props && `props: ${props}`].filter(Boolean).join("  ");
    return meta ? `${head}\n#  ${meta}` : head;
}
export function formatScene(doc, s, opts = {}) {
    const gutter = opts.plain ? 0 : Math.max(0, ...s.actions.map((a) => a.anchor.length));
    step("formatScene", `${s.anchor}: ${s.actions.length} actions, gutter=${gutter}`);
    return [formatSceneHead(doc, s), ...s.actions.map((a) => formatAction(doc, a, gutter, opts))].join("\n");
}
const ANCHOR_RE = /^([A-Za-z][A-Za-z0-9_]*|\d+(?:-\d+){0,2})\s\s+/;
/**
 * Parse one body line back. `known(name)` decides whether a `X：` prefix is a
 * real speaker — without it, an action line like `特写：她转身` would be
 * misread as dialogue.
 */
export function parseLine(line, known) {
    let rest = line.trimEnd();
    if (!rest.trim())
        return null;
    let anchor;
    const m = rest.match(ANCHOR_RE);
    if (m) {
        anchor = m[1];
        rest = rest.slice(m[0].length);
    }
    rest = rest.replace(/\\n/g, "\n");
    let ts;
    const tm = rest.match(/^((?:\d+:)?\d+:\d{2}(?:\.\d{1,3})?)\s/);
    if (tm) {
        ts = tm[1];
        rest = rest.slice(tm[0].length);
    }
    if (rest.startsWith("→"))
        return { anchor, ts, type: "transition", content: rest.slice(1).trim() };
    const colon = rest.indexOf("：");
    if (colon > 0) {
        const head = rest.slice(0, colon);
        const hm = head.match(/^([^()]+)(?:\((.*)\))?$/);
        if (hm && known(hm[1].trim())) {
            const notes = (hm[2] ?? "").split("/").map((s) => s.trim()).filter(Boolean);
            const thought = notes[0] === THOUGHT;
            const paren = (thought ? notes.slice(1) : notes).join("/");
            return {
                anchor, ts, type: thought ? "inner_thought" : "dialogue",
                speakerName: hm[1].trim(), paren: paren || undefined,
                content: rest.slice(colon + 1).trim(),
            };
        }
    }
    return { anchor, ts, type: "action", content: rest.trim() };
}
const SCENE_ID = /^(scn[1-9A-HJ-NP-Za-km-z]{6}|scn_\d+)$/;
const ENV_WORD = /^[A-Z.\/]+$/;
const COUNT = /^\d+\s+actions?$/;
const SPACE = { EXT: "exterior", EXTERIOR: "exterior", INT: "interior", INTERIOR: "interior" };
/**
 * Parse a `## …` scene header plus its optional `#  cast: … props: …` line.
 * Tokens are matched by shape, not position, so a hand-written new scene can
 * drop the ordinal, the id and the action count and still parse.
 */
/**
 * Split a scene block into its two header lines and its body. Positional, not
 * pattern-matched: an action line may legitimately start with `#`, and guessing
 * would silently swallow it.
 */
export function splitBlock(lines) {
    const i = lines.findIndex((l) => l.startsWith("##"));
    if (i < 0)
        return { body: lines };
    const hasMeta = lines[i + 1]?.startsWith("#") && !lines[i + 1].startsWith("##");
    const body = lines.filter((_, n) => n !== i && !(hasMeta && n === i + 1));
    return { head: lines[i], meta: hasMeta ? lines[i + 1] : undefined, body };
}
export function parseHead(headLine, metaLine) {
    const out = { env: {}, cast: [], props: [] };
    const toks = headLine.replace(/^##\s*/, "").split(/\s{2,}/).map((t) => t.trim()).filter(Boolean);
    for (const t of toks) {
        if (/^\d+-\d+$/.test(t) || COUNT.test(t))
            continue; // ordinal / derived count
        if (SCENE_ID.test(t)) {
            out.anchor = t;
            continue;
        }
        if (ENV_WORD.test(t) && t !== "—") {
            const [a, b] = t.split("/");
            if (a)
                out.env.space = SPACE[a] ?? a.toLowerCase();
            if (b)
                out.env.time = b.toLowerCase();
            continue;
        }
        if (t !== "—" && out.location === undefined)
            out.location = t;
    }
    if (metaLine) {
        const meta = metaLine.replace(/^#\s*/, "");
        const cast = meta.match(/cast:\s*([^]*?)(?:\s\sprops:|$)/);
        const props = meta.match(/props:\s*([^]*)$/);
        const split = (v) => (v ?? "").split(",").map((x) => x.trim()).filter(Boolean);
        out.cast = split(cast?.[1]);
        out.props = split(props?.[1]);
    }
    return out;
}
/** `名字(状态+状态)` -> name plus state names; a bare name means `default`. */
export function parseRefText(text) {
    const m = text.trim().match(/^(.*?)\s*\(([^)]*)\)$/);
    if (!m)
        return { name: text.trim(), states: [] };
    return { name: m[1].trim(), states: m[2].split("+").map((x) => x.trim()).filter(Boolean) };
}
